/**
 * Logging with optional tracking via injected callbacks.
 * Core does not depend on Sentry; the app calls setLogTracking() at init
 * with captureMessage/captureException from @sentry/react or @sentry/react-native.
 */

export type LogTracking = {
  captureMessage: (message: string, options?: unknown) => void | string;
  captureException: (exception: unknown, options?: unknown) => void | string;
  getErrorMessage?: (error: unknown) => string;
};

type LogOptions = {
  track?: boolean;
  level?: "log" | "info" | "warning" | "error" | "fatal" | "debug";
  [key: string]: unknown;
};

type LogErrorOptions = {
  disableTracking?: boolean;
  breadcrumbs?: Array<{
    level?: string;
    message?: string;
    category?: string;
    [key: string]: unknown;
  }>;
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
  [key: string]: unknown;
};

let tracking: LogTracking | null = null;

export function setLogTracking(t: LogTracking | null) {
  tracking = t;
}

function serializeUnknownForTracking(error: unknown): string {
  if (typeof error === "string") return error;
  try {
    const serialized = JSON.stringify(error);
    if (typeof serialized === "string" && serialized.length > 0) {
      return serialized;
    }
  } catch {
    // circular or otherwise unserializable — fall through
  }
  return "[unserializable error]";
}

function getErrorMessageForTracking(error: unknown): string {
  if (tracking?.getErrorMessage) return tracking.getErrorMessage(error);
  if (error instanceof Error) return error.message;
  return serializeUnknownForTracking(error);
}

/**
 * Basic logging. Writes to console; when options.track is true and tracking
 * is set, calls injected captureMessage.
 */
export function log(
  data: string | Record<string, unknown>,
  options?: LogOptions,
) {
  const level = options?.level ?? "log";
  const consoleLevel =
    level === "fatal" ? "error" : level === "warning" ? "warn" : level;

  // everything besides level and track is a field that completes the sentence
  const fields = Object.fromEntries(
    Object.entries(options ?? {}).filter(
      ([key]) => key !== "level" && key !== "track",
    ),
  );
  const hasFields = Object.keys(fields).length > 0;

  const method = console[
    consoleLevel as keyof Console
  ] as (typeof console)["log"];
  if (typeof method === "function") {
    const prefix = `[${String(level).toUpperCase()}]`;
    if (hasFields) method.call(console, prefix, data, fields);
    else method.call(console, prefix, data);
  }

  if (!options?.track || !tracking) return;

  try {
    const message = typeof data === "string" ? data : JSON.stringify(data);
    // sentry only keeps known capture context keys, so fields ride along as extras
    tracking.captureMessage(message, { level, extra: fields });
  } catch (err) {
    logError(err, { disableTracking: true });
  }
}

/**
 * Error logging. Always logs to console; when tracking is set and not
 * disableTracking, calls captureException.
 */
export function logError(error: unknown, options?: LogErrorOptions) {
  const message = getErrorMessageForTracking(error);
  const errorInstance =
    error instanceof Error ? error : new Error(message);

  console.error("[ERROR]", { message, errorInstance });

  if (options?.disableTracking || !tracking) return;

  try {
    tracking.captureException(errorInstance, options);
  } catch (_) {
    // avoid re-entrancy
  }
}

// Attach .error to log for API compatibility
log.error = logError;
